import { formatDate } from "@clipboard-health/date-time";
import { CBH_DATE_FORMAT } from "@clipboard-health/date-time/src/lib/format/constants";
import {
  IonAlert,
  IonBackButton,
  IonButton,
  IonButtons,
  IonCol,
  IonContent,
  IonHeader,
  IonPage,
  IonRow,
  IonTitle,
  IonToolbar,
} from "@ionic/react";
import { Button, Card, CardContent, Stack } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { useDefinedAgent } from "@src/app/store/helperHooks";
import { useToast } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { InlineLoader } from "@src/lib/deprecatedCode";
import { differenceInDays, endOfDay, format, startOfDay, startOfYear } from "date-fns";
import { useEffect, useState } from "react";

import { USER_EVENTS } from "../../../constants/userEvents";
import {
  createZendeskSupportTicketForProofEarning,
  fetchProofEarning,
  sendEmailProofEarning,
} from "../api";
import { useIsProofOfEarningsRevampEnabled } from "../featureFlagHooks";

export function ProofIncome() {
  const { email, userId } = useDefinedAgent();
  const { showErrorToast } = useToast();

  const [toDate, setToDate] = useState(new Date());
  const [fromDate, setFromDate] = useState(startOfYear(new Date()));
  const [isEdited, setEdited] = useState(false);
  const [showSupportScreen, setShowSupportScreen] = useState(false);
  const [html, setHtml] = useState("");
  const [loading, setLoading] = useState(false);
  const [showEmailScreen, setShowEmailScreen] = useState(false);

  const isProofOfEarningsRevampEnabled = useIsProofOfEarningsRevampEnabled();

  useEffect(() => {
    /**
     * FIXME - convert to `useGetQuery`
     */
    fetchProof();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchProof = async () => {
    setHtml("");
    setLoading(true);
    const proof = await fetchProofEarning({
      start: format(new Date(fromDate), "yyyy-MM-dd"),
      end: format(new Date(toDate), "yyyy-MM-dd"),
    });

    logEvent(USER_EVENTS.GENERATE_PROOF_INCOME, {
      startDate: format(new Date(fromDate), "yyyy-MM-dd"),
      endDate: format(new Date(toDate), "yyyy-MM-dd"),
      dateRange: differenceInDays(toDate, fromDate),
    });

    setEdited(false);
    setLoading(false);
    setHtml(proof);
  };

  const sendEmail = () => {
    if (toDate < fromDate) {
      showErrorToast("Please select a valid range.");
      return;
    }

    sendEmailProofEarning({
      start: format(new Date(fromDate), "yyyy-MM-dd"),
      end: format(new Date(toDate), "yyyy-MM-dd"),
    });

    logEvent(USER_EVENTS.EMAIL_PAYSTUB, {
      startDate: format(new Date(fromDate), "yyyy-MM-dd"),
      endDate: format(new Date(toDate), "yyyy-MM-dd"),
      dateRange: differenceInDays(toDate, fromDate),
      userId,
    });
  };

  const createZendeskTicket = async () => {
    const response = await createZendeskSupportTicketForProofEarning({
      start: format(new Date(fromDate), "yyyy-MM-dd"),
      end: format(new Date(toDate), "yyyy-MM-dd"),
    });

    if (response?.success) {
      logEvent(USER_EVENTS.NEED_HELP_PROOF_INCOME, {
        startDate: format(new Date(fromDate), "yyyy-MM-dd"),
        endDate: format(new Date(toDate), "yyyy-MM-dd"),
        dateRange: differenceInDays(toDate, fromDate),
      });
    } else {
      showErrorToast(`Error while sending the proof of income, please contact support.`);
    }
  };

  return (
    <IonPage>
      <IonHeader no-border>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton
              text=""
              defaultHref="/home/account"
              mode="ios"
              aria-label="Go back to Payroll page"
            />
          </IonButtons>
          {isProofOfEarningsRevampEnabled ? (
            <IonTitle>Proof of Earnings</IonTitle>
          ) : (
            <IonTitle>Proof of Income</IonTitle>
          )}
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <Card
          elevation={isProofOfEarningsRevampEnabled ? 0 : 3}
          style={{ marginTop: isProofOfEarningsRevampEnabled ? 10 : undefined }}
        >
          <CardContent>
            <Stack spacing={2}>
              <DatePicker
                label="Start Date"
                disableFuture
                value={fromDate}
                format={CBH_DATE_FORMAT}
                onChange={(value) => {
                  if (value) {
                    setEdited(true);
                    setFromDate(startOfDay(value));
                  }
                }}
              />
              <DatePicker
                label="End Date"
                disableFuture
                value={toDate}
                format={CBH_DATE_FORMAT}
                onChange={(value) => {
                  if (value) {
                    setEdited(true);
                    setToDate(endOfDay(value));
                  }
                }}
              />
              {!isProofOfEarningsRevampEnabled && isEdited && (
                <Button
                  fullWidth
                  variant="contained"
                  onClick={() => {
                    if (toDate < fromDate) {
                      showErrorToast("Please select a valid range.");
                      return;
                    }

                    fetchProof();
                  }}
                >
                  Confirm dates
                </Button>
              )}
              {isProofOfEarningsRevampEnabled && (
                <>
                  <IonButton
                    color="primary"
                    style={{ width: "100%" }}
                    onClick={() => setShowEmailScreen(true)}
                  >
                    Email me my proof of earnings
                  </IonButton>
                  <IonButton
                    color="danger"
                    style={{ width: "100%" }}
                    onClick={() => setShowSupportScreen(true)}
                  >
                    Is something wrong?
                  </IonButton>
                </>
              )}
            </Stack>
          </CardContent>
        </Card>
        {!isProofOfEarningsRevampEnabled && loading && (
          <div style={{ textAlign: "center", marginTop: 8 }}>
            <InlineLoader loading={true} />
          </div>
        )}

        {!isProofOfEarningsRevampEnabled && html && (
          <div className="bubbleContainer" style={{ margin: 8, padding: 16 }}>
            <h5>
              {formatDate(fromDate)} - {formatDate(toDate)} Proof of Earnings
            </h5>
            <div dangerouslySetInnerHTML={{ __html: html }} />
          </div>
        )}
        {!isProofOfEarningsRevampEnabled && (
          <div style={{ padding: 16 }}>
            <IonRow>
              <IonCol style={{ padding: isProofOfEarningsRevampEnabled ? 0 : undefined }}>
                <IonButton
                  color="primary"
                  style={{ width: "100%" }}
                  onClick={() => setShowEmailScreen(true)}
                >
                  Email Me My Proof Earnings
                </IonButton>
              </IonCol>
              <IonCol style={{ padding: isProofOfEarningsRevampEnabled ? 0 : undefined }}>
                <IonButton
                  color="danger"
                  style={{ width: "100%" }}
                  onClick={() => setShowSupportScreen(true)}
                >
                  Is something wrong?
                </IonButton>
              </IonCol>
            </IonRow>
          </div>
        )}
        <IonAlert
          isOpen={showEmailScreen}
          onDidDismiss={() => setShowEmailScreen(false)}
          header={"Email Your Proof of Earnings?"}
          message={`We'll send an email to ${email}`}
          backdropDismiss={true}
          buttons={[
            {
              text: "Never Mind",
              role: "cancel",
              handler: () => setShowEmailScreen(false),
            },
            {
              text: "Yes, send",
              handler: sendEmail,
            },
          ]}
        />
        <IonAlert
          isOpen={showSupportScreen}
          onDidDismiss={() => setShowSupportScreen(false)}
          header="Is something wrong"
          message="If there is something wrong with this document, please send it over email to our support team."
          backdropDismiss
          buttons={[
            {
              text: "Never Mind",
              role: "cancel",
              handler: () => setShowSupportScreen(false),
            },
            {
              text: "Yes, send",
              handler: createZendeskTicket,
            },
          ]}
        />
      </IonContent>
    </IonPage>
  );
}
